import { useState, useEffect } from "react";
import type { AppStage, BatchResult, Field, FieldType, DocumentIntelligence } from "@structurify/shared-types";
import { THEME, FONTS } from "./theme/index.ts";
import { CLAIM_FIELDS } from "./data/claimFields.ts";
import { DOC_INTELLIGENCE } from "./data/intelligence.ts";
import { initUpload, putFile, getDocumentStatus, type ExtractedField } from "./api/upload.ts";
import { saveSession, loadSession, clearSession, fetchMe, verifyMagicToken, logout } from "./api/auth.ts";
import { listWorkflows, type Workflow } from "./api/workflows.ts";
import { Chrome } from "./components/chrome/Chrome.tsx";
import { StageUpload } from "./stages/StageUpload.tsx";
import { StageAnalyze } from "./stages/StageAnalyze.tsx";
import { StageConfirm } from "./stages/StageConfirm.tsx";
import { StageReview } from "./stages/StageReview.tsx";
import { StageExport } from "./stages/StageExport.tsx";
import { StageWorkflow } from "./stages/StageWorkflow.tsx";
import { StageReturning } from "./stages/StageReturning.tsx";
import { StageBulk, StageBatchResult } from "./stages/StageBulk.tsx";
import { StageBatchIntelligence } from "./stages/StageBatchIntelligence.tsx";
import { StageCamera } from "./stages/StageCamera.tsx";
import { StageAudit } from "./stages/StageAudit.tsx";
import { StageAuth, Paywall, AccountMenu } from "./stages/StageAuth.tsx";

const FREE_LIMIT = 3;

const VALID_FIELD_TYPES = new Set<string>(["string", "date", "currency", "phone", "address", "long_text", "category"]);

function mapApiFields(apiFields: ExtractedField[]): Field[] {
  return apiFields.map((f, i) => ({
    id: f.name || `field_${i}`,
    label: f.label,
    value: f.value,
    type: (VALID_FIELD_TYPES.has(f.type) ? f.type : "string") as FieldType,
    confidence: f.confidence,
    page: 1,
    group: "Header" as const,
    uncertain: f.confidence < 0.8,
  }));
}

export function App() {
  const [stage, setStage] = useState<AppStage>("upload");
  const [progress, setProgress] = useState(0);
  const [statusLine, setStatusLine] = useState("READING DOCUMENT");
  const [authed, setAuthed] = useState(false);
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined);
  const [session, setSession] = useState<string | undefined>(undefined);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [batchResult, setBatchResult] = useState<BatchResult | null>(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);
  const [fields, setFields] = useState<Field[]>(CLAIM_FIELDS);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [documentType, setDocumentType] = useState<string | null>(null);
  const [isRealDoc, setIsRealDoc] = useState(false);
  const [uploadFileName, setUploadFileName] = useState<string | undefined>(undefined);
  const [uploadMimeType, setUploadMimeType] = useState<string | undefined>(undefined);
  const [intelligence, setIntelligence] = useState<DocumentIntelligence | null>(null);

  useEffect(() => {
    document.body.style.background = THEME.canvas;
    document.body.style.color = THEME.ink;
    document.body.style.fontFamily = FONTS.sans;
  }, []);

  // Detect magic token in URL (redirect from email link)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("magic_token");
    if (!token) return;

    // Remove token from URL immediately
    params.delete("magic_token");
    const newSearch = params.toString();
    window.history.replaceState({}, "", newSearch ? `?${newSearch}` : window.location.pathname);

    verifyMagicToken(token).then((user) => {
      saveSession(user);
      setSession(user.session);
      setUserEmail(user.email);
      setCreditsUsed(user.creditsUsed);
      setAuthed(true);
      setStage("returning");
    }).catch((err: unknown) => {
      console.error("[auth] magic token verify failed:", err);
    });
  }, []);

  // Restore session on app load
  useEffect(() => {
    const stored = loadSession();
    if (!stored) return;

    fetchMe(stored.session).then((user) => {
      if (user) {
        saveSession(user);
        setSession(user.session);
        setUserEmail(user.email);
        setCreditsUsed(user.creditsUsed);
        setAuthed(true);
      } else {
        clearSession();
      }
    }).catch(() => {
      clearSession();
    });
  }, []);

  // Fetch workflows whenever the session changes
  useEffect(() => {
    if (!session) { setWorkflows([]); return; }
    listWorkflows(session).then(setWorkflows).catch(() => setWorkflows([]));
  }, [session]);

  // Fake progress for non-file flows (camera, etc.)
  useEffect(() => {
    if (stage !== "analyze" || uploadFile) return;
    setProgress(0);
    setStatusLine("READING DOCUMENT");
    const lines: Array<[number, string]> = [
      [0.18, "READING DOCUMENT"],
      [0.42, "IDENTIFYING TYPE"],
      [0.68, "FINDING KEY FIELDS"],
      [0.88, "PREPARING REVIEW"],
      [1.00, "COMPLETE"],
    ];
    let i = 0;
    const tick = () => {
      if (i >= lines.length) {
        setCreditsUsed((c) => c + 1);
        setTimeout(() => setStage("confirm"), 280);
        return;
      }
      const line = lines[i];
      if (line) {
        setProgress(line[0]);
        setStatusLine(line[1]);
      }
      i++;
      setTimeout(tick, 520);
    };
    const t = setTimeout(tick, 280);
    return () => clearTimeout(t);
  }, [stage, uploadFile]);

  // Real upload flow
  useEffect(() => {
    if (!uploadFile) return;
    const file = uploadFile;
    let cancelled = false;

    (async () => {
      try {
        setProgress(0.1);
        setStatusLine("READING DOCUMENT");

        const { documentId, uploadUrl, token } = await initUpload(file, session, selectedWorkflow?.id);
        if (cancelled) return;

        setProgress(0.3);
        setStatusLine("UPLOADING FILE");
        await putFile(uploadUrl, token, file);
        if (cancelled) return;

        setProgress(0.5);
        setStatusLine("IDENTIFYING TYPE");

        for (let i = 0; i < 60; i++) {
          await new Promise<void>((r) => setTimeout(r, 2000));
          if (cancelled) return;

          const status = await getDocumentStatus(documentId);
          if (cancelled) return;

          if (status.status === "extracted" && status.fields) {
            setFields(mapApiFields(status.fields));
            setDocumentType(status.documentType);
            setIntelligence(status.intelligence);
            setIsRealDoc(true);
            setProgress(1);
            setStatusLine("COMPLETE");
            setCreditsUsed((c) => c + 1);
            // Refresh true credits count from DB
            if (session) fetchMe(session).then((u) => { if (u) { setCreditsUsed(u.creditsUsed); saveSession(u); } }).catch(() => undefined);
            setTimeout(() => {
              if (!cancelled) {
                setUploadFile(null);
                setStage("confirm");
              }
            }, 280);
            return;
          }

          if (status.status === "failed") {
            throw new Error(status.error ?? "Extraction failed");
          }

          setProgress(Math.min(0.5 + (i + 1) * 0.007, 0.9));
          if (i === 3) setStatusLine("FINDING KEY FIELDS");
          if (i === 8) setStatusLine("PREPARING REVIEW");
        }

        throw new Error("Extraction timed out");
      } catch (err) {
        if (cancelled) return;
        console.error("[upload]", err);
        setStatusLine("ERROR — PLEASE TRY AGAIN");
        setUploadFile(null);
        setTimeout(() => { if (!cancelled) setStage("upload"); }, 2000);
      }
    })();

    return () => { cancelled = true; };
  }, [uploadFile]);

  const tryStartNew = () => {
    if (!authed && creditsUsed >= FREE_LIMIT) {
      setPaywallOpen(true);
    } else {
      if (objectUrl) { URL.revokeObjectURL(objectUrl); setObjectUrl(null); }
      setIsRealDoc(false);
      setIntelligence(null);
      setSelectedWorkflow(null);
      setStage("upload");
    }
  };

  const tryDemo = () => {
    if (objectUrl) { URL.revokeObjectURL(objectUrl); setObjectUrl(null); }
    setFields(CLAIM_FIELDS);
    setDocumentType("P&C Insurance Claim — First Notice of Loss");
    setIsRealDoc(false);
    setIntelligence(null);
    setSelectedWorkflow(null);
    setStage("confirm");
  };

  const tryBulk = () => {
    if (!authed && creditsUsed >= FREE_LIMIT) {
      setPaywallOpen(true);
    } else {
      setStage("bulk");
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: THEME.canvas, color: THEME.ink, fontFamily: FONTS.sans, display: "flex", flexDirection: "column" }}>
      <Chrome
        theme={THEME} fonts={FONTS} stage={stage} setStage={setStage}
        authed={authed} userEmail={userEmail} creditsUsed={creditsUsed} freeLimit={FREE_LIMIT}
        onAccount={() => setAccountOpen(true)}
        onSignIn={() => setStage("signin")}
      />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {stage === "upload" && (
          <StageUpload theme={THEME} fonts={FONTS}
            workflow={selectedWorkflow ?? undefined}
            onDemo={tryDemo}
            onUpload={(file) => {
              if (objectUrl) URL.revokeObjectURL(objectUrl);
              setObjectUrl(URL.createObjectURL(file));
              setUploadFileName(file.name);
              setUploadMimeType(file.type);
              setIsRealDoc(false);
              setUploadFile(file);
              setStage("analyze");
            }}
            onBulk={() => setStage("bulk")}
            onScan={() => setStage("camera")}
          />
        )}
        {stage === "analyze" && (
          <StageAnalyze
            theme={THEME} fonts={FONTS} progress={progress} statusLine={statusLine}
            fileName={uploadFileName}
            fileSize={uploadFile?.size}
            mimeType={uploadMimeType}
            objectUrl={objectUrl}
          />
        )}
        {stage === "confirm" && (
          <StageConfirm theme={THEME} fonts={FONTS} fields={fields}
            documentType={documentType}
            onConfirm={() => setStage("review")}
            onAdjust={() => {}}
          />
        )}
        {stage === "review" && (
          <StageReview theme={THEME} fonts={FONTS} fields={fields} layout="split"
            onExport={() => setStage("export")}
            intel={isRealDoc ? intelligence : DOC_INTELLIGENCE}
            focusFieldId={focusFieldId}
            fileName={isRealDoc ? uploadFileName : "claim_form_whitfield.pdf"}
            objectUrl={isRealDoc ? objectUrl : null}
            mimeType={isRealDoc ? uploadMimeType : undefined}
          />
        )}
        {stage === "export" && (
          <StageExport theme={THEME} fonts={FONTS} fields={fields}
            onSaveWorkflow={() => setStage("workflow")}
            onDone={() => {
              if (!authed && creditsUsed >= FREE_LIMIT) setPaywallOpen(true);
              else setStage("returning");
            }}
          />
        )}
        {stage === "workflow" && (
          <StageWorkflow theme={THEME} fonts={FONTS}
            fields={fields}
            documentType={documentType}
            session={session}
            onFinish={() => setStage("returning")}
            onSaved={(id) => {
              if (session) listWorkflows(session).then(setWorkflows).catch(() => undefined);
              console.log("[workflow] saved", id);
            }}
          />
        )}
        {stage === "returning" && (
          <StageReturning theme={THEME} fonts={FONTS}
            onNew={tryStartNew}
            onBulk={tryBulk}
            onWorkflow={(wf) => { setSelectedWorkflow(wf); setStage("upload"); }}
            onAudit={() => setStage("audit")}
            onScan={() => setStage("camera")}
            authed={authed} creditsUsed={creditsUsed} freeLimit={FREE_LIMIT}
            workflows={workflows}
          />
        )}
        {stage === "bulk" && (
          <StageBulk theme={THEME} fonts={FONTS}
            session={session}
            onComplete={(result) => {
              setBatchResult(result);
              setCreditsUsed((c) => c + result.total);
              setStage("batch-result");
            }}
            onCancel={() => setStage("returning")}
          />
        )}
        {stage === "batch-result" && (
          <StageBatchResult theme={THEME} fonts={FONTS} batch={batchResult}
            onReviewQueue={() => { setFocusFieldId("estimated_amount"); setStage("review"); }}
            onExport={() => setStage("export")}
            onIntelligence={() => setStage("batch-intel")}
            onRowClick={() => { setFocusFieldId("estimated_amount"); setStage("review"); }}
          />
        )}
        {stage === "batch-intel" && (
          <StageBatchIntelligence theme={THEME} fonts={FONTS} batch={batchResult}
            onBack={() => setStage("batch-result")}
            onReviewQueue={() => { setFocusFieldId("estimated_amount"); setStage("review"); }}
            onExport={() => setStage("export")}
          />
        )}
        {stage === "camera" && (
          <StageCamera theme={THEME} fonts={FONTS}
            onCapture={(file) => {
              if (objectUrl) URL.revokeObjectURL(objectUrl);
              setObjectUrl(URL.createObjectURL(file));
              setUploadFileName(file.name);
              setUploadMimeType(file.type);
              setIsRealDoc(false);
              setUploadFile(file);
              setStage("analyze");
            }}
            onCancel={() => setStage("upload")}
          />
        )}
        {stage === "audit" && (
          <StageAudit theme={THEME} fonts={FONTS}
            session={session}
            onBack={() => setStage(authed || creditsUsed > 0 ? "returning" : "upload")}
          />
        )}
        {stage === "signin" && (
          <StageAuth theme={THEME} fonts={FONTS} mode="signin"
            onSubmit={() => { setAuthed(true); setStage("returning"); }}
            onSwitch={() => setStage("signup")}
          />
        )}
        {stage === "signup" && (
          <StageAuth theme={THEME} fonts={FONTS} mode="signup"
            onSubmit={() => { setAuthed(true); setStage("returning"); }}
            onSwitch={() => setStage("signin")}
          />
        )}
      </div>

      {paywallOpen && (
        <Paywall theme={THEME} fonts={FONTS}
          creditsUsed={creditsUsed} freeLimit={FREE_LIMIT}
          onClose={() => setPaywallOpen(false)}
          onSignUp={() => { setPaywallOpen(false); setStage("signup"); }}
          onSignIn={() => { setPaywallOpen(false); setStage("signin"); }}
        />
      )}

      {accountOpen && (
        <AccountMenu theme={THEME} fonts={FONTS}
          authed={authed} creditsUsed={creditsUsed} freeLimit={FREE_LIMIT}
          userEmail={userEmail}
          onClose={() => setAccountOpen(false)}
          onSignIn={() => { setAccountOpen(false); setStage("signin"); }}
          onSignUp={() => { setAccountOpen(false); setStage("signup"); }}
          onSignOut={() => {
            if (session) {
              logout(session).catch((err: unknown) => console.error("[auth] logout failed:", err));
            }
            clearSession();
            setSession(undefined);
            setUserEmail(undefined);
            setAuthed(false);
            setAccountOpen(false);
          }}
        />
      )}
    </div>
  );
}
